// Athena document guard, client side (2026-10-06).
//
// The gateways now file a document into a chart once. Two answers need a person:
//   409 {code: "already_filed",  needs_confirm: true}  this file is already in the chart
//   409 {code: "athena_unknown", needs_confirm: true}  an earlier send got no answer from
//                                                      Athena, so it may already be there
//   409 {code: "duplicate_upload", needs_confirm: true}  this file is already on the chart's
//                                                      timeline (Documents tab / Send to EMR)
// Both clients (api.js, careiqapi.js) ask once with the server's message; on OK the SAME
// request goes again with ?confirm_resend=1, which the gateway honours for that send.
// Cancel leaves the original error to the caller (its toast shows the message).
//
// The gateways only answer needs_confirm to a client that says it can show this dialog:
// every request to a document-filing route carries ?confirm_capable=1 (withConfirmCapable,
// added by both request interceptors). Without it (care prod's older build, the OSA
// build) the gateway never asks: it answers an exact repeat with the copy already there
// and lets anything else through, so those screens are never stuck (review 10-06). A
// query parameter, not a header: v1's CORS allow-list would reject a new header.

export const NEEDS_CONFIRM_CODES = Object.freeze(["already_filed", "athena_unknown", "duplicate_upload"]);

// Document-filing routes of both gateways (path only; matched anywhere in the URL).
const CONFIRM_CAPABLE_ROUTES = [
  /\/add-strepcovidflu-athena$/,                         // in-house COVID/STREP/FLU result
  /\/fax-documents\/add-(imaging-result|clinical-document)$/, // fax Send to Athena
  /\/v1\/\d+\/add-(imaging-result|encounter-document)$/,     // generic upload routes
  /\/documents\/clinical-document$/,                       // v1 generic clinical document
  /\/forms\/questionnaire\/\d+\/upload_and_send_athena$/,   // questionnaire PDF
  /\/imaging\/generate-pdf$/,                              // worksheets -> Athena
  /\/fhir-mirror\/\d+\/patient\/\d+\/documents\/upload$/,   // Documents tab / Send to EMR
  /\/fhir-mirror\/\d+\/patient\/\d+\/documents\/local\/\d+\/sync$/, // Sync to Athena
  /\/formwiz\/submissions\/[^/]+\/send-to-emr$/,           // Form Wizard Send to EMR
];

export const isConfirmCapableRoute = (url) => {
  const path = String(url || "").split("?")[0].split("#")[0];
  return CONFIRM_CAPABLE_ROUTES.some((re) => re.test(path));
};

/** Request interceptor step: mark document-filing calls with ?confirm_capable=1. */
export function withConfirmCapable(config) {
  if (config && isConfirmCapableRoute(config.url)) {
    config.params = { ...(config.params || {}), confirm_capable: 1 };
  }
  return config;
}

export const athenaConfirmPrompt = (data) =>
  `${data?.message || "This document may already be in the patient's chart."}\n\n` +
  (data?.code === "duplicate_upload" ? "Upload it again anyway?" : "Send it to Athena anyway?");

const defaultConfirm = (message) =>
  typeof window !== "undefined" && typeof window.confirm === "function" && window.confirm(message);

/**
 * For an axios error that is a 409 needs_confirm answer: ask, and on OK return the
 * replayed request (a promise). Otherwise return null (not such an answer, already
 * confirmed once, or the person said no).
 */
export function maybeConfirmResend(client, error, confirmFn = defaultConfirm) {
  const cfg = error && error.config;
  const resp = error && error.response;
  if (!cfg || !resp || resp.status !== 409 || cfg._confirmResend) return null;
  const d = resp.data;
  if (!d || typeof d !== "object" || d.needs_confirm !== true || !NEEDS_CONFIRM_CODES.includes(d.code)) {
    return null;
  }
  let ok = false;
  try {
    ok = !!confirmFn(athenaConfirmPrompt(d));
  } catch (e) {
    ok = false;
  }
  if (!ok) return null;
  cfg._confirmResend = true;
  cfg.params = { ...(cfg.params || {}), confirm_resend: 1 };
  return client(cfg);
}

/** A short line for a toast from a gateway error answer (falls back to `fallback`). */
export const athenaErrorMessage = (error, fallback) => {
  const d = error?.response?.data;
  if (d && typeof d === "object" && typeof d.message === "string" && d.message) return d.message;
  return fallback;
};
